import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { theme } from 'antd';

import { useStores } from '@/hooks/use-stores';
import gtag from '@/utils/gtag';
import ConditionalToolTip from '@/utils/ConditionalTooltip';

import { EPClickableText, EPTierTooltipGlobalStyle } from './styled';
import EPRequestTierModal from './EPRequestTierModal';
import LockIcon from './icons/LockIcon';

const { useToken } = theme;

export interface EPTierTooltipProps {
  children?: React.ReactElement;
  shouldShowTooltip?: boolean;
  title?: React.ReactNode;
  textPart1?: string | React.ReactElement;
  textPart2?: string | React.ReactElement;
  clickableText?: string;
  tooltipPlacement?: string;
  tooltipWidth?: number;
  initialIsTierRequestModalOpen?: boolean;
  onModalCloseHandler?: () => void;
  emailRequestTypeOfInterestedPackage?: string;
  emailRequestButtonClicked?: string;
  emailRequestWhichScreen?: string;
  customMessage?: string;
}

const EPTierTooltip = ({
  children,
  shouldShowTooltip = true,
  title,
  textPart1,
  textPart2,
  clickableText,
  tooltipPlacement,
  tooltipWidth,
  initialIsTierRequestModalOpen,
  emailRequestTypeOfInterestedPackage,
  emailRequestButtonClicked,
  emailRequestWhichScreen,
  customMessage,
  onModalCloseHandler,
}: EPTierTooltipProps) => {
  const [isTierRequestModalOpen, setIsTierRequestModalOpen] = useState(initialIsTierRequestModalOpen);
  const { stationStore } = useStores();
  const { t } = useTranslation();
  const { token } = useToken();

  useEffect(() => {
    setIsTierRequestModalOpen(initialIsTierRequestModalOpen);
    return () => setIsTierRequestModalOpen(false);
  }, [initialIsTierRequestModalOpen]);

  const onClick = (buttonClicked?: string) => {
    stationStore.updateRequestTier({
      emailRequestTypeOfInterestedPackage,
      emailRequestButtonClicked: `${emailRequestButtonClicked} - clicked ON ${buttonClicked}`,
      emailRequestWhichScreen,
      customMessage,
    });
    setIsTierRequestModalOpen(true);
    gtag.campaignClick('EcoPlantLight', emailRequestWhichScreen, buttonClicked);
  };

  const onModalClose = () => {
    if (onModalCloseHandler) onModalCloseHandler();
    setIsTierRequestModalOpen(false);
  };

  return (
    <>
      <EPTierTooltipGlobalStyle />
      <ConditionalToolTip
        placement={tooltipPlacement ?? 'right'}
        overlayClassName="tier-tooltip"
        overlayStyle={{ width: tooltipWidth ?? 380 }}
        content={
          <div className="tier-tooltip-container">
            <div>
              <LockIcon
                className="tier-tooltip-icon-lock"
                onClick={() => onClick('lock icon')}
                color={token.colorText}
              />
            </div>
            <div>
              {title && <div className="tier-tooltip-title">{title}</div>}
              <div>
                {textPart1 && textPart1}
                {
                  <EPClickableText onClick={() => onClick('Click Here')}>
                    {clickableText ?? t('clickHere')}
                  </EPClickableText>
                }
                {textPart2 && textPart2}
              </div>
            </div>
          </div>
        }
        condition={shouldShowTooltip}
      >
        {children}
      </ConditionalToolTip>
      <EPRequestTierModal isOpen={isTierRequestModalOpen} onClose={onModalClose} />
    </>
  );
};

export default EPTierTooltip;
